import { CommonModule, DatePipe, registerLocaleData } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  NgModule,
} from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router, RouteReuseStrategy, RouterModule } from "@angular/router";
import {
  ApiModule,
  Configuration as ConfigurationLexiClient,
} from "@lexi-clients/lexi";
import { OidcUaaModule } from "@lexi/oidc-uaa";
import * as Sentry from "@sentry/angular";
import { TraceService } from "@sentry/angular";
import {
  // 🚀 OPTIMISATION: Modules DevExtreme critiques pour le démarrage uniquement
  // Navigation et layout principal
  DxButtonModule,
  DxLoadIndicatorModule,
  DxTemplateModule,
  DxScrollViewModule,
  DxDrawerModule,
  DxToolbarModule,
  DxMenuModule,

  // Interface d'authentification et header
  DxPopupModule,
  DxSelectBoxModule,

  // Modules requis par les composants déclarés dans app.module
  DxDataGridModule,
  DxFormModule,
  DxTextBoxModule,
  DxCheckBoxModule,
  DxDateBoxModule,
  DxNumberBoxModule,
  DxTextAreaModule,
  DxListModule,
  DxTreeListModule,
  DxFileUploaderModule,
  DxPopoverModule,
  DxTooltipModule,
  DxProgressBarModule,
  DxValidationGroupModule,
  DxRadioGroupModule,
  DxSwitchModule,
  DxTabsModule,
  DxBoxModule,
  DxDropDownButtonModule,
  DxTabPanelModule,
  DxAccordionModule,
  DxMultiViewModule,
  DxGalleryModule,
  DxFileManagerModule
} from "devextreme-angular";
import { loadMessages, locale } from "devextreme/localization";
import { environment } from "../environments/environment";
import { AppInitService } from "./app-init.service";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { ArticleAssociationComponent } from "./components/article-association/article-association.component";
import { ArticleDetailComponent } from "./components/article-detail/article-detail.component";
import { ArticleListComponent } from "./components/article-list/article-list.component";
import { ClassificationDatagridComponent } from "./components/classification-datagrid/classification-datagrid.component";
import { ClientPrixListComponent } from "./components/client-prix-list/client-prix-list.component";
import { ConditionnementListComponent } from "./components/conditionnement-list/conditionnement-list.component";
import { ConnaissementInfoComponent } from "./components/connaissement-info/connaissement-info.component";
import { ConnaissementListComponent } from "./components/connaissement-list/connaissement-list.component";
import { ContactsPageComponent } from "./components/contacts-page/contacts-page.component";
import { CreationSocieteComponent } from "./components/creation-societe/creation-societe.component";
import { EvenementTypeListComponent } from "./components/evenement-type-list/evenement-type-list.component";
import { FactureListComponent } from "./components/facture-list/facture-list.component";
import { FormuleComponent } from "./components/formule/formule.component";
import { ListingArticlesComponent } from "./components/listing-articles/listing-articles.component";
import { PartenaireDetailComponent } from "./components/partenaire/partenaire-detail/partenaire-detail.component";
import { PartenaireDoublonsDetailsComponent } from "./components/partenaire/partenaire-doublons-list/partenaire-doublons-details/partenaire-doublons-details/partenaire-doublons-details.component";
import { PartenaireDoublonsListComponent } from "./components/partenaire/partenaire-doublons-list/partenaire-doublons-list.component";
import { PartenaireFormComponent } from "./components/partenaire/partenaire-form/partenaire-form.component";
import { PartenaireListComponent } from "./components/partenaire/partenaire-list/partenaire-list.component";
import { PartenaireNewFormComponent } from "./components/partenaire/partenaire-new-form/partenaire-new-form.component";
import { SettingsComponent } from "./components/settings/settings.component";
import { TarifArticleHistoriqueComponent } from "./components/tarif-article-historique/tarif-article-historique.component";
import { TarifArticleListComponent } from "./components/tarif-article-list/tarif-article-list.component";
import { TarifArticleSimplifieComponent } from "./components/tarif-article-simplifie/tarif-article-simplifie.component";
import { TransporteurListComponent } from "./components/transporteur-list/transporteur-list.component";
import { UserProfileComponent } from "./components/user-profile/user-profile.component";
import { VariantesArticleListComponent } from "./components/variantes-article-list/variantes-article-list.component";
import { VoirEnTantQueComponent } from "./components/voir-en-tant-que/voir-en-tant-que.component";
import { AuthHttpInterceptor } from "./interceptors/auth-http-interceptor";
import { ErrorHttpInterceptor } from "./interceptors/error-http-interceptor";
import { ScopeHeaderHttpInterceptor } from "./interceptors/scope-header-http-interceptor";
import { SideNavOuterToolbarComponent } from "./layouts";
import { ReferencesConnaissementService } from "./modules/fret-local/connaissements/references/references-connaissement.service";
import { SharedModule } from "./modules/shared/shared.module";
import { TracabiliteArticleComponent } from "./modules/shared/tracabilite-article/tracabilite-article.component";
import { ThemeService } from './services/theme.service';
import { LexiStorage } from "./settings/lexi-storage";
import { AppFooterModule, SideNavigationMenuModule } from "./shared/components";
import { FormPopupModule } from "./shared/components/form-popup/form-popup.component";
import { HeaderModule } from "./shared/components/header/header.module";
import { ToolbarFormModule } from "./shared/components/toolbar-form/toolbar-form.component";
import { CustomReuseStrategy } from "./shared/custom-reusestrategie";
import { AppInfoService, ScreenService } from "./shared/services";
import { CalculInfoPrixService } from "./shared/services/calcul-info-prix.service";
import { DxDataSourceService } from "./shared/services/dx-data-source.service";
import { ExportDataGridService } from "./shared/services/export-data-grid.service";
import { ModuleService } from "./shared/services/module.service";
import { NavigationService } from "./shared/services/navigation-service";
import { ObjectTypeService } from "./shared/services/object-type.service";
import { ObjectUtilitiesService } from "./shared/services/object-utilities.service";
import { ArticleTarifsFretComponent } from "./components/article-detail/article-tarifs-fret/article-tarifs-fret.component";
import { WatermarkComponent } from "./shared/components/watermark/watermark.component";
import { WatermarkService } from "./core/services/watermark.service";

registerLocaleData(localeFr);

declare const require: any;
const frMessages = require("devextreme/localization/messages/fr.json");

loadMessages(frMessages);
locale("fr");

@NgModule({
  declarations: [
    AppComponent,
    ArticleListComponent,
    ArticleDetailComponent,
    ArticleTarifsFretComponent,
    PartenaireListComponent,
    PartenaireDoublonsListComponent,
    PartenaireDoublonsDetailsComponent,
    EvenementTypeListComponent,
    PartenaireDetailComponent,
    PartenaireFormComponent,
    PartenaireNewFormComponent,
    CreationSocieteComponent,
    SettingsComponent,
    UserProfileComponent,
    ConnaissementInfoComponent,
    TransporteurListComponent,
    FactureListComponent,
    ConnaissementListComponent,
    ConditionnementListComponent,
    TarifArticleListComponent,
    ClassificationDatagridComponent,
    ClientPrixListComponent,
    VoirEnTantQueComponent,
    TarifArticleSimplifieComponent,
    TracabiliteArticleComponent,
    SideNavOuterToolbarComponent,
    VariantesArticleListComponent,
    ContactsPageComponent,
    TarifArticleHistoriqueComponent,
    ListingArticlesComponent,
    FormuleComponent,
    ArticleAssociationComponent,
    WatermarkComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    HeaderModule,
    AppRoutingModule,
    OidcUaaModule.forRoot({
      baseServiceUrl: environment.baseServiceUrl,
      authWellKnownEndpoints: null,
      openIDConfiguration: null,
      shouldInitializeConfig: null,
    }),
    // 🚀 OPTIMISATION: DevExtreme modules critiques pour le démarrage uniquement
    // Navigation et layout principal
    DxButtonModule,
    DxLoadIndicatorModule,
    DxTemplateModule,
    DxScrollViewModule,
    DxDrawerModule,
    DxToolbarModule,
    DxMenuModule,

    // Interface d'authentification et header
    DxPopupModule,
    DxSelectBoxModule,

    // Modules requis par les composants déclarés dans app.module
    DxDataGridModule,
    DxFormModule,
    DxTextBoxModule,
    DxCheckBoxModule,
    DxDateBoxModule,
    DxNumberBoxModule,
    DxTextAreaModule,
    DxListModule,
    DxTreeListModule,
    DxFileUploaderModule,
    DxPopoverModule,
    DxTooltipModule,
    DxProgressBarModule,
    DxValidationGroupModule,
    DxRadioGroupModule,
    DxSwitchModule,
    DxTabsModule,
    DxBoxModule,
    DxDropDownButtonModule,
    DxTabPanelModule,
    DxAccordionModule,
    DxMultiViewModule,
    DxGalleryModule,
    DxFileManagerModule,
    // Modules internes
    SideNavigationMenuModule,
    ToolbarFormModule,
    FormPopupModule,
    HttpClientModule,
    ApiModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    RouterModule,
    AppFooterModule
  ],
  providers: [
    DatePipe,
    {
      // 🚀 OPTIMISATION: Chargement Crisp en différé (non-bloquant au démarrage)
      // Configuration chargée en arrière-plan après authentification
      provide: APP_INITIALIZER,
      useFactory: (appInit: AppInitService) => () => {
        console.info("APP INITIALIZATION - Crisp (non-bloquant)");
        // Retour immédiat - chargement asynchrone en arrière-plan
        appInit.loadCrispConfiguration();
        return Promise.resolve();
      },
      deps: [AppInitService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    {
      // 🚀 OPTIMISATION: Sentry chargé en priorité basse (après premier rendu)
      // Configuration et init différées pour ne pas bloquer le rendu initial
      provide: APP_INITIALIZER,
      useFactory: (appInit: AppInitService) => () => {
        console.info("APP INITIALIZATION - Sentry (différé)");
        // Différer l'initialisation Sentry après le premier rendu
        setTimeout(() => {
          appInit.loadSentryConfigurationAndInitSdk();
        }, 100);
        return Promise.resolve();
      },
      deps: [AppInitService],
      multi: true,
    },
    {
      // Utilise par défaut le localStorage, à l'avenir on pourrait surchager ça.
      provide: LexiStorage,
      useValue: localStorage,
    },
    /* 🚀 OPTIMISATION: Services initialisés de manière non-bloquante */
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => Promise.resolve(),
      deps: [NavigationService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ScopeHeaderHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    // Déclaration des Token Configuration des @lexi-clients
    // Utilisé pour définir le basePath des requêtes HTTP.
    // Le basePath est affecté dans AppInitService
    {
      provide: ConfigurationLexiClient,
      useValue: new ConfigurationLexiClient(),
    },
    { provide: LOCALE_ID, useValue: "fr-FR" },
    ScreenService,
    AppInfoService,
    AppFooterModule,
    ModuleService,
    ReferencesConnaissementService,
    ObjectTypeService,
    DxDataSourceService,
    ExportDataGridService,
    CalculInfoPrixService,
    ThemeService,
    WatermarkService,
    ObjectUtilitiesService,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      // 🚀 OPTIMISATION: TraceService initialisé de manière non-bloquante
      provide: APP_INITIALIZER,
      useFactory: () => () => Promise.resolve(),
      deps: [TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (appInit: AppInitService) => () => {
        console.info("APP INITIALIZATION - Watermark");
        appInit.loadWatermarkConfiguration();
        return Promise.resolve();
      },
      deps: [AppInitService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
